import {
  Document,
  Page,
  View,
  Text,
  StyleSheet,
  Svg,
  Path,
} from "@react-pdf/renderer";

export interface ChangeOrderPdfLineItem {
  aiaItemCode: string | number;
  description: string;
  amount: number;
  subContractorName?: string;
}

export interface ChangeOrderPdfAttachment {
  fileName: string;
}

export interface ChangeOrderPdfProps {
  projectName?: string;
  coNumber: string;
  date: string;
  title: string;
  trade: string;
  reason: string;
  scheduleImpact: string;
  contractDateImpact?: string;
  description: string;
  scopeNotes?: string;
  lineItems: ChangeOrderPdfLineItem[];
  totalAmount: number;
  attachments: ChangeOrderPdfAttachment[];
  checklist: Record<string, boolean>;
  baseContract: number;
  approvedCOsTotal: number;
  coImpactAmount: number;
  projectedContract: number;
  lender?: { companyName?: string };
}

// Same palette used across every export in this app (App.scss :root vars)
// so this document reads as part of the same family as G702Pdf/SovPdf/etc.
const COLORS = {
  b: "#14508c",
  bdk: "#0d3a66",
  bd: "#dde1e6",
  tx: "#111111",
  tx2: "#555c66",
  tx3: "#909399",
  green: "#1a6b35",
  amber: "#7a5c00",
  red: "#a32d2d",
  bg: "#f4f5f6",
};

const fmt = (val: number | undefined | null): string => {
  const n = Number(val || 0);
  const sign = n < 0 ? "-" : n > 0 ? "+" : "";
  return `${sign}$${Math.abs(n).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const S = StyleSheet.create({
  page: {
    paddingTop: 32,
    paddingBottom: 40,
    paddingHorizontal: 36,
    fontFamily: "Helvetica",
    fontSize: 9,
    color: COLORS.tx,
    backgroundColor: "#ffffff",
  },

  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.bd,
  },
  headerTitle: {
    fontFamily: "Helvetica-Bold",
    fontSize: 15,
    color: COLORS.b,
    marginBottom: 4,
  },
  headerSub: { fontSize: 8.5, color: COLORS.tx2 },
  headerRight: { alignItems: "flex-end" },
  headerMetaLine: { fontSize: 8, color: COLORS.tx2, marginBottom: 2 },

  card: {
    borderWidth: 0.75,
    borderColor: COLORS.bd,
    borderRadius: 10,
    paddingVertical: 14,
    paddingHorizontal: 16,
    backgroundColor: "#ffffff",
    marginBottom: 12,
  },
  cardTitle: {
    fontFamily: "Helvetica-Bold",
    fontSize: 9.5,
    color: COLORS.tx2,
    letterSpacing: 0.5,
    marginBottom: 10,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.bd,
  },

  // Two-column key/value grid, mirrors the on-screen "Review & Submit" grid.
  // Built as explicit paired rows rather than flexWrap: react-pdf's layout
  // engine has unreliable flexWrap support and can silently clip/drop
  // content instead of erroring, so every PDF in this codebase (G702Pdf,
  // SovPdf) builds explicit rows instead of relying on it.
  gridRow: { flexDirection: "row", marginBottom: 9 },
  gridCell: { flex: 1, paddingRight: 10 },
  gridLabel: { fontSize: 7.5, color: COLORS.tx3, marginBottom: 2 },
  gridValue: { fontSize: 9.5, fontFamily: "Helvetica-Bold", color: COLORS.tx },

  bodyText: { fontSize: 9, color: COLORS.tx, lineHeight: 1.5 },
  bodyLabel: {
    fontSize: 7.5,
    color: COLORS.tx3,
    marginBottom: 4,
    marginTop: 8,
  },

  // Financial impact strip
  impactStrip: {
    flexDirection: "row",
    backgroundColor: COLORS.bg,
    borderRadius: 6,
    paddingVertical: 10,
    paddingHorizontal: 12,
    marginBottom: 12,
  },
  impactCell: { flex: 1, alignItems: "center" },
  impactDivider: { width: 1, backgroundColor: COLORS.bd, marginVertical: 2 },
  impactLabel: { fontSize: 7, color: COLORS.tx3, marginBottom: 3 },
  impactValue: { fontFamily: "Helvetica-Bold", fontSize: 11 },

  // Line items table
  tHead: {
    flexDirection: "row",
    backgroundColor: COLORS.bdk,
    paddingVertical: 6,
    paddingHorizontal: 6,
    borderRadius: 3,
  },
  tRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0",
    paddingVertical: 6,
    paddingHorizontal: 6,
  },
  tRowAlt: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0",
    paddingVertical: 6,
    paddingHorizontal: 6,
    backgroundColor: "#fafafa",
  },
  tTotal: {
    flexDirection: "row",
    backgroundColor: "#edf1f5",
    paddingVertical: 8,
    paddingHorizontal: 6,
    borderRadius: 3,
    marginTop: 3,
  },
  th: { fontFamily: "Helvetica-Bold", fontSize: 8.5, color: "#ffffff" },
  td: { fontSize: 8.5, color: COLORS.tx },
  tdMuted: { fontSize: 8.5, color: COLORS.tx3 },
  tdBold: { fontFamily: "Helvetica-Bold", fontSize: 9 },

  cCode: { width: 55 },
  cDesc: { flex: 1 },
  cVendor: { width: 130, paddingLeft: 8 },
  cVal: { width: 90, textAlign: "right" },
  cPct: { width: 55, textAlign: "right" },

  // Attachments / checklist
  listRow: { flexDirection: "row", alignItems: "center", marginBottom: 6 },
  listBullet: { fontSize: 8.5, color: COLORS.b, width: 12 },
  listText: { fontSize: 8.5, color: COLORS.tx },
  // Drawn as a box rather than a Unicode check/circle glyph — the standard
  // Helvetica font only supports WinAnsi-encoded characters, so symbols
  // like ✓/○ render as garbled glyphs (e.g. "Ë") instead of erroring.
  checkBox: {
    width: 10,
    height: 10,
    borderRadius: 2,
    borderWidth: 1,
    borderColor: COLORS.tx3,
    marginRight: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  checkBoxChecked: {
    backgroundColor: COLORS.green,
    borderColor: COLORS.green,
  },

  certificationBox: {
    borderWidth: 0.75,
    borderColor: COLORS.bd,
    borderRadius: 8,
    backgroundColor: COLORS.bg,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  certificationText: { fontSize: 8, color: COLORS.tx2, lineHeight: 1.5 },

  footer: {
    position: "absolute",
    bottom: 20,
    left: 36,
    right: 36,
    flexDirection: "row",
    justifyContent: "space-between",
    borderTopWidth: 1,
    borderTopColor: COLORS.bd,
    paddingTop: 6,
  },
  footerText: { fontSize: 7.5, color: COLORS.tx3 },
});

export default function ChangeOrderPdf({
  projectName,
  coNumber,
  date,
  title,
  trade,
  reason,
  scheduleImpact,
  contractDateImpact,
  description,
  scopeNotes,
  lineItems,
  totalAmount,
  attachments,
  checklist,
  baseContract,
  approvedCOsTotal,
  coImpactAmount,
  projectedContract,
  lender,
}: ChangeOrderPdfProps) {
  const today = new Date().toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const checklistEntries = Object.entries(checklist ?? {});

  return (
    <Document>
      <Page size="A4" style={S.page}>
        <View style={S.headerRow}>
          <View>
            <Text style={S.headerTitle}>Change Order — {coNumber}</Text>
            <Text style={S.headerSub}>
              {projectName ?? "Construction Project"}
            </Text>
          </View>
          <View style={S.headerRight}>
            <Text style={S.headerMetaLine}>Generated: {today}</Text>
            {lender?.companyName ? (
              <Text style={S.headerMetaLine}>Lender: {lender.companyName}</Text>
            ) : null}
          </View>
        </View>

        <View style={S.card}>
          <Text style={S.cardTitle}>CHANGE ORDER DETAILS</Text>
          <View style={S.gridRow} wrap={false}>
            <View style={S.gridCell}>
              <Text style={S.gridLabel}>CO NUMBER</Text>
              <Text style={S.gridValue}>{coNumber || "—"}</Text>
            </View>
            <View style={S.gridCell}>
              <Text style={S.gridLabel}>DATE</Text>
              <Text style={S.gridValue}>{date || "—"}</Text>
            </View>
          </View>
          <View style={S.gridRow} wrap={false}>
            <View style={S.gridCell}>
              <Text style={S.gridLabel}>TRADE</Text>
              <Text style={S.gridValue}>{trade || "—"}</Text>
            </View>
            <View style={S.gridCell}>
              <Text style={S.gridLabel}>REASON</Text>
              <Text style={S.gridValue}>{reason || "—"}</Text>
            </View>
          </View>
          <View style={S.gridRow} wrap={false}>
            <View style={S.gridCell}>
              <Text style={S.gridLabel}>SCHEDULE IMPACT</Text>
              <Text style={S.gridValue}>{scheduleImpact || "None"}</Text>
            </View>
            <View style={S.gridCell}>
              <Text style={S.gridLabel}>REVISED COMPLETION DATE</Text>
              <Text style={S.gridValue}>{contractDateImpact || "—"}</Text>
            </View>
          </View>
          <View style={S.gridRow} wrap={false}>
            <View style={S.gridCell}>
              <Text style={S.gridLabel}>TOTAL AMOUNT</Text>
              <Text style={[S.gridValue, { color: COLORS.green }]}>
                {fmt(totalAmount)}
              </Text>
            </View>
            <View style={S.gridCell}>
              <Text style={S.gridLabel}>DOCUMENTS</Text>
              <Text style={S.gridValue}>{attachments.length} attached</Text>
            </View>
          </View>

          <Text style={S.bodyLabel}>TITLE</Text>
          <Text style={S.bodyText}>{title || "—"}</Text>

          <Text style={S.bodyLabel}>DESCRIPTION</Text>
          <Text style={S.bodyText}>{description || "—"}</Text>

          {scopeNotes ? (
            <>
              <Text style={S.bodyLabel}>ADDITIONAL SCOPE NOTES</Text>
              <Text style={S.bodyText}>{scopeNotes}</Text>
            </>
          ) : null}
        </View>

        <View style={S.impactStrip}>
          <View style={S.impactCell}>
            <Text style={S.impactLabel}>ORIGINAL CONTRACT</Text>
            <Text style={[S.impactValue, { color: COLORS.tx }]}>
              {fmt(baseContract)}
            </Text>
          </View>
          <View style={S.impactDivider} />
          <View style={S.impactCell}>
            <Text style={S.impactLabel}>APPROVED COs</Text>
            <Text style={[S.impactValue, { color: COLORS.tx }]}>
              {fmt(approvedCOsTotal)}
            </Text>
          </View>
          <View style={S.impactDivider} />
          <View style={S.impactCell}>
            <Text style={S.impactLabel}>THIS CO</Text>
            <Text
              style={[
                S.impactValue,
                { color: coImpactAmount >= 0 ? COLORS.green : COLORS.red },
              ]}
            >
              {fmt(coImpactAmount)}
            </Text>
          </View>
          <View style={S.impactDivider} />
          <View style={S.impactCell}>
            <Text style={S.impactLabel}>NEW REVISED CONTRACT</Text>
            <Text style={[S.impactValue, { color: COLORS.b }]}>
              {fmt(projectedContract)}
            </Text>
          </View>
        </View>

        <View style={S.card}>
          <Text style={S.cardTitle}>LINE ITEMS</Text>

          <View style={S.tHead}>
            <Text style={[S.th, S.cCode]}>AIA Item</Text>
            <Text style={[S.th, S.cDesc]}>Description</Text>
            <Text style={[S.th, S.cVendor]}>Subcontractor / Vendor</Text>
            <Text style={[S.th, S.cVal]}>Amount</Text>
            <Text style={[S.th, S.cPct]}>% of CO</Text>
          </View>

          {lineItems.map((item, i) => {
            const amt = Number(item.amount) || 0;
            const pct =
              totalAmount > 0 ? ((amt / totalAmount) * 100).toFixed(1) : "0.0";
            return (
              <View
                key={i}
                style={i % 2 === 0 ? S.tRow : S.tRowAlt}
                wrap={false}
              >
                <Text style={[S.td, S.cCode]}>{item.aiaItemCode ?? "—"}</Text>
                <Text style={[S.td, S.cDesc]}>{item.description || "—"}</Text>
                <Text style={[S.td, S.cVendor]}>
                  {item.subContractorName || "—"}
                </Text>
                <Text style={[S.td, S.cVal]}>{fmt(amt)}</Text>
                <Text style={[S.tdMuted, S.cPct]}>{pct}%</Text>
              </View>
            );
          })}

          <View style={S.tTotal} wrap={false}>
            <Text style={[S.tdBold, S.cCode]} />
            <Text style={[S.tdBold, S.cDesc]}>Total Change Order Amount</Text>
            <Text style={[S.tdBold, S.cVendor]} />
            <Text style={[S.tdBold, S.cVal]}>{fmt(totalAmount)}</Text>
            <Text style={[S.tdMuted, S.cPct]}>
              {totalAmount > 0 ? "100.0%" : "0.0%"}
            </Text>
          </View>
        </View>

        {attachments.length > 0 && (
          <View style={S.card}>
            <Text style={S.cardTitle}>SUPPORTING DOCUMENTS</Text>
            {attachments.map((att, i) => (
              <View key={i} style={S.listRow}>
                <Text style={S.listBullet}>•</Text>
                <Text style={S.listText}>{att.fileName}</Text>
              </View>
            ))}
          </View>
        )}

        {checklistEntries.length > 0 && (
          <View style={S.card} wrap={false}>
            <Text style={S.cardTitle}>SUBMISSION CHECKLIST</Text>
            {checklistEntries.map(([label, checked]) => (
              <View key={label} style={S.listRow}>
                <View
                  style={checked ? [S.checkBox, S.checkBoxChecked] : S.checkBox}
                >
                  {checked ? (
                    <Svg width={7} height={7} viewBox="0 0 10 10">
                      <Path
                        d="M1.5 5.2L4 7.7L8.5 2.3"
                        stroke="#ffffff"
                        strokeWidth={1.6}
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </Svg>
                  ) : null}
                </View>
                <Text style={S.listText}>{label}</Text>
              </View>
            ))}
          </View>
        )}

        <View style={S.certificationBox} wrap={false}>
          <Text style={S.certificationText}>
            By submitting this change order, the General Contractor certifies
            that the work described constitutes a legitimate change to the
            contract scope, that the cost breakdown is accurate and reflects
            actual costs, and that this change has been communicated to all
            relevant parties. CSC New England was notified of this
            submission. The project owner has final approval authority.
          </Text>
        </View>

        <View style={S.footer} fixed>
          <Text style={S.footerText}>
            {projectName ?? "Construction Project"} · Change Order {coNumber} ·{" "}
            {today}
          </Text>
          <Text
            style={S.footerText}
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
}
